import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { PASSWORD_CONFIG } from '../../../../../shared/config/passwordConfig';

// Password strength validation logic
export const validatePassword = (password, formatMessage) => {
  if (!password) return { score: 0, requirements: [], strength: 'none' };

  const requirements = [
    {
      id: PASSWORD_CONFIG.REQUIREMENTS.LENGTH,
      test: password.length >= PASSWORD_CONFIG.MIN_LENGTH,
      text: formatMessage(
        {
          id: 'AccountSettings.PasswordRequirement.Length',
          defaultMessage: 'At least {minLength} characters',
        },
        { minLength: PASSWORD_CONFIG.MIN_LENGTH },
      ),
    },
    {
      id: PASSWORD_CONFIG.REQUIREMENTS.UPPERCASE,
      test: PASSWORD_CONFIG.PATTERNS.UPPERCASE.test(password),
      text: formatMessage({
        id: 'AccountSettings.PasswordRequirement.Uppercase',
        defaultMessage: 'One uppercase letter',
      }),
    },
    {
      id: PASSWORD_CONFIG.REQUIREMENTS.LOWERCASE,
      test: PASSWORD_CONFIG.PATTERNS.LOWERCASE.test(password),
      text: formatMessage({
        id: 'AccountSettings.PasswordRequirement.Lowercase',
        defaultMessage: 'One lowercase letter',
      }),
    },
    {
      id: PASSWORD_CONFIG.REQUIREMENTS.NUMBER,
      test: PASSWORD_CONFIG.PATTERNS.NUMBER.test(password),
      text: formatMessage({
        id: 'AccountSettings.PasswordRequirement.Number',
        defaultMessage: 'One number',
      }),
    },
    {
      id: PASSWORD_CONFIG.REQUIREMENTS.SPECIAL,
      test: PASSWORD_CONFIG.PATTERNS.SPECIAL.test(password),
      text: formatMessage({
        id: 'AccountSettings.PasswordRequirement.Special',
        defaultMessage: 'One special character',
      }),
    },
  ];

  const passedCount = requirements.filter(req => req.test).length;
  const score = (passedCount / requirements.length) * 100;

  // Length requirement is MANDATORY - must always pass for strong password
  const lengthRequirement = requirements.find(
    req => req.id === PASSWORD_CONFIG.REQUIREMENTS.LENGTH,
  );
  const hasMinLength = lengthRequirement && lengthRequirement.test;

  let strength = 'weak';
  if (score >= PASSWORD_CONFIG.STRENGTH_THRESHOLDS.STRONG && hasMinLength) strength = 'strong';
  else if (score >= PASSWORD_CONFIG.STRENGTH_THRESHOLDS.MEDIUM) strength = 'medium';
  else if (score >= PASSWORD_CONFIG.STRENGTH_THRESHOLDS.FAIR) strength = 'fair';

  return {
    score,
    requirements,
    strength,
    isStrong: score >= PASSWORD_CONFIG.STRENGTH_THRESHOLDS.STRONG && hasMinLength,
  };
};

const PasswordStrengthIndicator = ({
  password,
  maxWidth = 400,
  marginTop = -2,
  marginBottom = 0,
  showOnlyWhenPassword = true,
}) => {
  const { formatMessage } = useIntl();
  const passwordStrength = validatePassword(password, formatMessage);

  // Don't render if no password and showOnlyWhenPassword is true
  if (showOnlyWhenPassword && !password) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', mt: marginTop, mb: marginBottom }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5, gap: 1, width: '100%' }}>
        <LinearProgress
          variant="determinate"
          value={passwordStrength.score}
          sx={{
            height: 6,
            flexGrow: 1,
            borderRadius: 1,
            backgroundColor: 'grey.200',
            mb: 1,
            '& .MuiLinearProgress-bar': {
              backgroundColor:
                passwordStrength.strength === 'strong'
                  ? 'success.main'
                  : passwordStrength.strength === 'medium'
                    ? 'warning.main'
                    : passwordStrength.strength === 'fair'
                      ? 'warning.main'
                      : 'error.main',
            },
          }}
        />

        <Typography
          variant="body2"
          component="span"
          sx={{
            fontWeight: 500,
            fontSize: '0.8rem',
            color:
              passwordStrength.strength === 'strong'
                ? 'success.main'
                : passwordStrength.strength === 'medium'
                  ? 'warning.main'
                  : passwordStrength.strength === 'fair'
                    ? 'warning.main'
                    : 'error.main',
          }}
        >
          {formatMessage({
            id: `AccountSettings.PasswordStrength.${passwordStrength.strength.charAt(0).toUpperCase() + passwordStrength.strength.slice(1)}`,
            defaultMessage:
              passwordStrength.strength.charAt(0).toUpperCase() +
              passwordStrength.strength.slice(1),
          })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {passwordStrength.requirements.map(req => (
          <Box key={req.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {req.test ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography component="span" variant="body2" sx={{ fontSize: '0.8rem' }}>
              {req.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

PasswordStrengthIndicator.propTypes = {
  password: PropTypes.string.isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  marginTop: PropTypes.number,
  marginBottom: PropTypes.number,
  showOnlyWhenPassword: PropTypes.bool,
};

export default PasswordStrengthIndicator;
